/**
 * 共享对话框 - 支持项目/知识库/企业的分享管理
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { XMarkIcon, PlusIcon, TrashIcon, UserGroupIcon } from '@heroicons/react/24/outline';
import { shareApi, getErrorMessage } from '../services/api';
import type { EntityType, SharePermission, ShareInfo, UserSearchItem } from '../types';

interface ShareDialogProps {
  isOpen: boolean;
  onClose: () => void;
  entityType: EntityType;
  entityId: number;
  entityName: string;
  isOwner: boolean;
}

const ENTITY_TYPE_LABELS: Record<EntityType, string> = {
  project: '项目',
  knowledge_base: '知识库',
  company_profile: '企业',
};

const ShareDialog: React.FC<ShareDialogProps> = ({
  isOpen,
  onClose,
  entityType,
  entityId,
  entityName,
  isOwner,
}) => {
  const [query, setQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState<UserSearchItem[]>([]);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [checkedUser, setCheckedUser] = useState<UserSearchItem | null>(null);
  const timerRef = useRef<number | null>(null);
  const [permission, setPermission] = useState<SharePermission>('read');
  const [sharing, setSharing] = useState(false);
  const [shares, setShares] = useState<ShareInfo[]>([]);
  const [loadingShares, setLoadingShares] = useState(false);
  const [updatingId, setUpdatingId] = useState<number | null>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const clearMsg = () => {
    setTimeout(() => setMessage(null), 3000);
  };

  const showMsg = (type: 'success' | 'error', text: string) => {
    setMessage({ type, text });
    clearMsg();
  };

  const loadShares = useCallback(async () => {
    if (!isOpen) return;
    try {
      setLoadingShares(true);
      const res = await shareApi.sharedByMe(entityType, entityId);
      setShares(res.data.shares);
    } catch {
      // 静默失败
    } finally {
      setLoadingShares(false);
    }
  }, [isOpen, entityType, entityId]);

  useEffect(() => {
    if (isOpen) {
      loadShares();
      setQuery('');
      setResults([]);
      setDropdownOpen(false);
      setSearching(false);
      setCheckedUser(null);
      setPermission('read');
      setMessage(null);
      if (timerRef.current) window.clearTimeout(timerRef.current);
    }
  }, [isOpen, loadShares]);

  const runSearch = useCallback(async (kw: string) => {
    const trimmed = kw.trim();
    if (!trimmed) {
      setResults([]);
      setDropdownOpen(false);
      return;
    }
    try {
      setSearching(true);
      const res = await shareApi.searchUsers(trimmed);
      setResults(res.data.users || []);
      setDropdownOpen(true);
    } catch {
      setResults([]);
      setDropdownOpen(false);
    } finally {
      setSearching(false);
    }
  }, []);

  const onQueryChange = (v: string) => {
    setQuery(v);
    setCheckedUser(null);
    if (timerRef.current) window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(() => runSearch(v), 250);
  };

  const selectUser = (u: UserSearchItem) => {
    setCheckedUser(u);
    setQuery(`${u.display_name}（${u.username}）`);
    setDropdownOpen(false);
    setResults([]);
  };

  const handleShare = async () => {
    if (!checkedUser) return;
    try {
      setSharing(true);
      setMessage(null);
      await shareApi.create({
        entity_type: entityType,
        entity_id: entityId,
        username: checkedUser.username,
        permission,
      });
      showMsg('success', `已分享给 ${checkedUser.display_name || checkedUser.username}`);
      setQuery('');
      setResults([]);
      setDropdownOpen(false);
      setCheckedUser(null);
      setPermission('read');
      await loadShares();
    } catch (error) {
      showMsg('error', getErrorMessage(error, '分享失败'));
    } finally {
      setSharing(false);
    }
  };

  const handleUpdatePermission = async (shareId: number, newPermission: SharePermission) => {
    try {
      setUpdatingId(shareId);
      setMessage(null);
      await shareApi.updatePermission(shareId, newPermission);
      // 本地更新状态，避免重新加载
      setShares(prev => prev.map(s => s.id === shareId ? { ...s, permission: newPermission } : s));
      showMsg('success', `权限已更新为「${newPermission === 'edit' ? '可编辑' : '只读'}」`);
    } catch (error) {
      showMsg('error', getErrorMessage(error, '更新权限失败'));
    } finally {
      setUpdatingId(null);
    }
  };

  const handleRevoke = async (share: ShareInfo) => {
    if (!window.confirm(`确定要取消对「${share.shared_with_name}」的分享吗？`)) return;
    try {
      await shareApi.revoke(share.id);
      showMsg('success', `已取消分享`);
      await loadShares();
    } catch (error) {
      showMsg('error', getErrorMessage(error, '撤销分享失败'));
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* 背景遮罩 */}
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />

      {/* 弹窗 */}
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-md mx-4 max-h-[85vh] flex flex-col">
        {/* 标题栏 */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">分享设置</h2>
            <p className="text-xs text-gray-500 mt-0.5">
              {ENTITY_TYPE_LABELS[entityType]}：{entityName}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        {/* 内容区 */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {/* 消息提示 */}
          {message && (
            <div className={`px-3 py-2 rounded-lg text-xs ${
              message.type === 'success'
                ? 'bg-green-50 text-green-700 border border-green-200'
                : 'bg-red-50 text-red-700 border border-red-200'
            }`}>
              {message.text}
            </div>
          )}

          {/* 添加分享（仅所有者可见） */}
          {isOwner && (
            <div className="space-y-3">
              <h3 className="text-sm font-medium text-gray-700">添加分享</h3>

              {/* 用户名模糊搜索 + 下拉选择 */}
              <div className="relative">
                {!checkedUser ? (
                  <>
                    <input
                      type="text"
                      value={query}
                      onChange={(e) => onQueryChange(e.target.value)}
                      onFocus={() => results.length > 0 && setDropdownOpen(true)}
                      onBlur={() => setTimeout(() => setDropdownOpen(false), 150)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          if (timerRef.current) window.clearTimeout(timerRef.current);
                          runSearch(query);
                        }
                      }}
                      placeholder="输入用户名或昵称搜索"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    {searching && (
                      <span className="absolute right-3 top-2.5 text-xs text-gray-400">搜索中…</span>
                    )}
                    {dropdownOpen && (
                      <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-auto">
                        {results.length === 0 ? (
                          <div className="px-3 py-2 text-sm text-gray-400">无匹配用户</div>
                        ) : (
                          results.map((u) => (
                            <div
                              key={u.id}
                              onClick={() => selectUser(u)}
                              className="px-3 py-2 text-sm hover:bg-blue-50 cursor-pointer flex items-center gap-2"
                            >
                              <span className="font-medium text-gray-900 truncate">{u.display_name}</span>
                              <span className="text-xs text-gray-400">{u.username}</span>
                            </div>
                          ))
                        )}
                      </div>
                    )}
                  </>
                ) : (
                  <div className="flex items-center gap-2">
                    <span className="inline-flex items-center rounded-full bg-blue-50 px-3 py-1 text-sm text-blue-700">
                      {checkedUser.display_name}（{checkedUser.username}）
                    </span>
                    <button
                      type="button"
                      onClick={() => {
                        setCheckedUser(null);
                        setQuery('');
                        setResults([]);
                        setDropdownOpen(false);
                      }}
                      className="text-xs text-gray-400 hover:text-gray-600"
                    >
                      重新选择
                    </button>
                  </div>
                )}
              </div>

              {/* 用户已选 → 显示权限选择和确认按钮 */}
              {checkedUser && (
                <div className="bg-blue-50/50 border border-blue-200 rounded-lg p-3 space-y-3">
                  <div className="flex items-center gap-2 text-sm">
                    <UserGroupIcon className="w-4 h-4 text-blue-500" />
                    <span className="font-medium text-gray-900">
                      {checkedUser.display_name || checkedUser.username}
                    </span>
                    <span className="text-xs text-gray-400">({checkedUser.username})</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <label className="text-xs text-gray-600 font-medium">权限：</label>
                    <select
                      value={permission}
                      onChange={(e) => setPermission(e.target.value as SharePermission)}
                      className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="read">只读</option>
                      <option value="edit">可编辑</option>
                    </select>
                    <button
                      onClick={handleShare}
                      disabled={sharing}
                      className="ml-auto inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 transition-colors"
                    >
                      <PlusIcon className="w-3.5 h-3.5 mr-1" />
                      {sharing ? '分享中...' : '确认分享'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* 当前分享列表 */}
          <div className="space-y-2">
            <h3 className="text-sm font-medium text-gray-700">
              {isOwner ? '已分享给' : '当前的共享'}
            </h3>
            {loadingShares ? (
              <div className="text-center py-4 text-xs text-gray-400">加载中...</div>
            ) : shares.length === 0 ? (
              <div className="text-center py-6">
                <UserGroupIcon className="mx-auto h-8 w-8 text-gray-300 mb-1" />
                <p className="text-xs text-gray-400">
                  {isOwner ? '暂未分享给任何人' : '暂无共享信息'}
                </p>
              </div>
            ) : (
              <div className="space-y-1.5">
                {shares.map((s) => (
                  <div
                    key={s.id}
                    className="flex items-center justify-between px-3 py-2.5 bg-gray-50 rounded-lg"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <UserGroupIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />
                      <div className="min-w-0">
                        <span className="text-sm text-gray-900 truncate block">
                          {s.shared_with_name}
                        </span>
                        {isOwner ? (
                          <div className="flex items-center gap-1">
                            <select
                              value={s.permission}
                              onChange={(e) => handleUpdatePermission(s.id, e.target.value as SharePermission)}
                              disabled={updatingId === s.id}
                              className="text-[10px] font-medium rounded border border-gray-300 bg-white px-1 py-0.5 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:opacity-50"
                            >
                              <option value="read">只读</option>
                              <option value="edit">可编辑</option>
                            </select>
                            {updatingId === s.id && (
                              <span className="text-[10px] text-gray-400">保存中...</span>
                            )}
                          </div>
                        ) : (
                          <span className={`text-[10px] font-medium ${
                            s.permission === 'edit' ? 'text-orange-600' : 'text-blue-600'
                          }`}>
                            {s.permission === 'edit' ? '可编辑' : '只读'}
                          </span>
                        )}
                      </div>
                    </div>
                    {isOwner && (
                      <button
                        onClick={() => handleRevoke(s)}
                        className="p-1 rounded text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors flex-shrink-0"
                        title="撤销分享"
                      >
                        <TrashIcon className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* 底部 */}
        <div className="px-5 py-3 border-t border-gray-200 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium rounded-lg text-gray-700 bg-gray-100 hover:bg-gray-200 transition-colors"
          >
            关闭
          </button>
        </div>
      </div>
    </div>
  );
};

export default ShareDialog;
